import type { onUpdateProps } from "@waldner-laboreinrichtungen/component-system";
import {
  Button,
  Clock,
  FlexBox,
  Modal,
  space,
  ToggleButton,
  ToggleSwitch,
  Typography,
} from "@waldner-laboreinrichtungen/component-system";
import type { Dispatch, SetStateAction } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import type { DeviceWithAutomations, WeekDays } from "../view-model";
import { WEEK_DAYS } from "../view-model";

type TimeControlModalProps = {
  isOpenModal: boolean;
  setIsOpenModal: Dispatch<SetStateAction<boolean>>;
  setIsUsedForAllDevices: Dispatch<SetStateAction<boolean>>;
  isUsedForAllDevices: boolean;
  device: DeviceWithAutomations;
  commandType: "light" | "operation";
  onChangeDisabled?: () => void;
  onSubmit: (props: OnSubmitProps) => void;
  disabled?: boolean;
  selectedDays: WeekDays[];
  initialEndTime?: string;
  initialStartTime?: string;
  onHandleCloseModal: () => void;
  onSelectDays: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

export type OnSubmitProps = {
  isUsedForAllDevices: boolean;
  startTime: string;
  endTime: string;
  selectedDays: WeekDays[];
  device: DeviceWithAutomations;
  commandType: "light" | "operation";
};

export const TimeControlModal = ({
  commandType,
  device,
  disabled,
  initialEndTime = "18:00",
  initialStartTime = "08:00",
  isOpenModal,
  isUsedForAllDevices,
  onChangeDisabled,
  onHandleCloseModal,
  onSelectDays,
  onSubmit,
  selectedDays,
  setIsUsedForAllDevices,
}: TimeControlModalProps) => {
  const { t } = useTranslation();
  const [start, setStartTime] = useState(initialStartTime);
  const [end, setEndTime] = useState(initialEndTime);

  useEffect(() => {
    setStartTime(initialStartTime);
    setEndTime(initialEndTime);
  }, [initialEndTime, initialStartTime, isOpenModal]);

  const onUpdate = ({ endTime, startTime }: onUpdateProps) => {
    if (
      /^\d{2}:\d{2}$/.test(startTime.h + ":" + startTime.m) &&
      /^\d{2}:\d{2}$/.test(endTime.h + ":" + endTime.m)
    ) {
      setStartTime(startTime.h + ":" + startTime.m);
      setEndTime(endTime.h + ":" + endTime.m);
    }
  };

  return (
    <Modal onClose={onHandleCloseModal} open={isOpenModal}>
      <FlexBox flexDirection="column" gap={space(8)} justifyContent="flex-end">
        <FlexBox
          alignItems="center"
          gap={space(15)}
          justifyContent="space-between"
        >
          <Typography>{`${t("channel." + commandType)} - ${
            device?.name
          }`}</Typography>
          <FlexBox alignItems="center" gap={space(3)}>
            <Typography size="xs">{t("timecontrol.activate")}</Typography>
            <ToggleSwitch checked={!disabled} onChange={onChangeDisabled} />
          </FlexBox>
        </FlexBox>
        <FlexBox justifyContent="center" marginBottom={space(6)} width="100%">
          <Clock
            disabled={disabled}
            initialEndTime={end}
            initialStartTime={start}
            onUpdate={onUpdate}
            toText={t("timecontrol.to") || ""}
          />
        </FlexBox>
        <Typography size="sm">{t("timecontrol.ondays")}</Typography>
        <FlexBox gap={space(2)}>
          {WEEK_DAYS.map((day) => (
            <ToggleButton
              checked={
                day === selectedDays.find((selectedDay) => selectedDay === day)
              }
              height="40px"
              key={day}
              onChange={onSelectDays}
              value={day}
              width="40px"
            >
              {t(`timecontrol.${day}`)}
            </ToggleButton>
          ))}
        </FlexBox>
        <FlexBox gap={space(3)}>
          <input
            id="useForAllDevices"
            name="useForAllDevices"
            onChange={(e) => setIsUsedForAllDevices(e.target.checked)}
            type="checkbox"
            value="useForAllDevices"
          />
          <label htmlFor="useForAllDevices">
            <Typography size="xs">{t("timecontrol.alldevices")}</Typography>
          </label>
        </FlexBox>
        <FlexBox gap={space(4)}>
          <Button onClick={onHandleCloseModal} variant="secondary">
            {t("timecontrol.cancel")}
          </Button>
          <Button
            onClick={() =>
              onSubmit({
                startTime: start,
                endTime: end,
                isUsedForAllDevices,
                selectedDays: selectedDays,
                device,
                commandType,
              })
            }
          >
            {t("timecontrol.save")}
          </Button>
        </FlexBox>
      </FlexBox>
    </Modal>
  );
};
